/**
 * BookingPassengerSection - Display and edit passengers/luggage
 */
import React from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Users, Briefcase, Edit2, Plus, Minus } from 'lucide-react';

const BookingPassengerSection = ({
  editData,
  setEditData,
  editMode,
  setEditMode,
  canEdit
}) => {
  return (
    <div className="grid grid-cols-2 gap-2 sm:gap-3">
      {/* Passengers - Editable */}
      <div 
        className={`bg-[#0B0B0B] rounded-lg p-2.5 sm:p-3 ${canEdit ? 'cursor-pointer hover:border-[#C9A24D]/50 border border-transparent active:bg-[#0B0B0B]/80' : ''}`}
        onClick={() => canEdit && editMode !== 'passengers' && setEditMode('passengers')}
        data-testid="passengers-section"
      >
        <div className="flex items-center justify-between mb-1">
          <p className="text-[#E5E5E5]/50 text-[10px] sm:text-xs uppercase flex items-center gap-1">
            <Users className="w-3 h-3" /> Passagers
          </p>
          {canEdit && editMode !== 'passengers' && <Edit2 className="w-3 h-3 text-[#C9A24D]/50" />}
        </div>
        
        {editMode === 'passengers' ? (
          <div className="space-y-2" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-center gap-2 sm:gap-3">
              <button
                onClick={() => setEditData(prev => ({ ...prev, passengers: Math.max(1, prev.passengers - 1) }))}
                className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-[#C9A24D]/20 text-[#C9A24D] flex items-center justify-center"
                data-testid="decrease-passengers"
              >
                <Minus className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              </button>
              <span className="text-[#E5E5E5] text-lg sm:text-xl font-bold w-6 sm:w-8 text-center" data-testid="passenger-count">{editData.passengers}</span>
              <button
                onClick={() => setEditData(prev => ({ ...prev, passengers: Math.min(8, prev.passengers + 1) }))}
                className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-[#C9A24D]/20 text-[#C9A24D] flex items-center justify-center"
                data-testid="increase-passengers"
              >
                <Plus className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              </button>
            </div>
            {/* Passenger names */}
            <div className="space-y-1 mt-2">
              {Array.from({ length: editData.passengers }).map((_, i) => (
                <Input
                  key={i}
                  value={editData.passengerNames[i] || ''}
                  onChange={(e) => {
                    const names = [...editData.passengerNames];
                    names[i] = e.target.value;
                    setEditData(prev => ({ ...prev, passengerNames: names }));
                  }}
                  placeholder={`Passager ${i + 1}`}
                  className="bg-[#1F1F1F] border-[#C9A24D]/20 text-[#E5E5E5] text-[10px] sm:text-xs h-7 sm:h-8"
                  data-testid={`passenger-name-${i}`}
                />
              ))}
            </div>
            <Button size="sm" variant="ghost" onClick={() => setEditMode(null)} className="text-[#C9A24D] text-[10px] sm:text-xs h-7 px-2">
              OK
            </Button>
          </div>
        ) : (
          <p className="text-[#E5E5E5] text-base sm:text-lg font-semibold">{editData.passengers}</p>
        )}
      </div>

      {/* Luggage */}
      <div 
        className={`bg-[#0B0B0B] rounded-lg p-2.5 sm:p-3 ${canEdit ? 'cursor-pointer hover:border-[#C9A24D]/50 border border-transparent active:bg-[#0B0B0B]/80' : ''}`}
        onClick={() => canEdit && editMode !== 'luggage' && setEditMode('luggage')}
        data-testid="luggage-section"
      >
        <p className="text-[#E5E5E5]/50 text-[10px] sm:text-xs uppercase flex items-center gap-1 mb-1">
          <Briefcase className="w-3 h-3" /> Bagages
        </p>
        {editMode === 'luggage' ? (
          <div className="flex items-center justify-center gap-2 sm:gap-3" onClick={e => e.stopPropagation()}>
            <button
              onClick={() => setEditData(prev => ({ ...prev, luggage: Math.max(0, prev.luggage - 1) }))}
              className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-[#C9A24D]/20 text-[#C9A24D] flex items-center justify-center"
              data-testid="decrease-luggage"
            >
              <Minus className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            </button>
            <span className="text-[#E5E5E5] text-lg sm:text-xl font-bold w-6 sm:w-8 text-center" data-testid="luggage-count">{editData.luggage}</span>
            <button
              onClick={() => setEditData(prev => ({ ...prev, luggage: Math.min(10, prev.luggage + 1) }))}
              className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-[#C9A24D]/20 text-[#C9A24D] flex items-center justify-center"
              data-testid="increase-luggage"
            >
              <Plus className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            </button>
          </div>
        ) : (
          <p className="text-[#E5E5E5] text-base sm:text-lg font-semibold">{editData.luggage}</p>
        )}
      </div>
    </div>
  );
};

export default BookingPassengerSection;
